import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
} from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import jwtDecode from 'jwt-decode';

const FetchContext = createContext({
  authAxios: null,
  isAdmin: false,
  canAdd: false,
  canManageAdasSubscriptions: false,
  canReadAll: false,
  canCreateSecurityCodes: false,
  canManageUsers: false,
  canManageAdasPackages: false,
  canManageAdasTargets: false,
  canManageIsoRigs: false,
});
const { Provider } = FetchContext;

const FetchProvider = ({ children }) => {
  const [accessToken, setAccessToken] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [canAdd, setCanAdd] = useState(false);
  const [canReadAll, setCanReadAll] = useState(false);
  const [canCreateSecurityCodes, setCanCreateSecurityCodes] = useState(false);
  const [canManageUsers, setCanManageUsers] = useState(false);
  const [canManageAdasSubscriptions, setCanManageAdasSubscriptions] = useState(false);
  const [canManageAdasPackages, setCanManageAdasPackages] = useState(false);
  const [canManageAdasTargets, setCanManageAdasTargets] = useState(false);
  const [canManageIsoRigs, setCanManageIsoRigs] = useState(false);
  const { getAccessTokenSilently, isAuthenticated, isLoading } = useAuth0();

  const getToken = async () => {
    // Wait for Auth0 to finish loading
    if (isLoading) return;

    if (isAuthenticated) {
      try {
        const token = await getAccessTokenSilently();
        setAccessToken(token);

        const decodedToken = jwtDecode(token);
        setIsAdmin(decodedToken.permissions.includes('write:all'));
        setCanAdd(decodedToken.permissions.includes('create:all'));
        setCanReadAll(decodedToken.permissions.includes('read:all'));
        setCanCreateSecurityCodes(decodedToken.permissions.includes('create:securitycodes'));
        setCanManageUsers(decodedToken.permissions.includes('manage:users'));
        setCanManageAdasSubscriptions(decodedToken.permissions.includes('manage:adassubscriptions'));
        setCanManageAdasPackages(decodedToken.permissions.includes('manage:adaspackages'));
        setCanManageAdasTargets(decodedToken.permissions.includes('manage:adastargets'));
        setCanManageIsoRigs(decodedToken.permissions.includes('manage:isorigs'));
      } catch (error) {
        console.error(error);
      }
    }
  };

  useEffect(() => {
    getToken();
  }, [isAuthenticated, isLoading]);

  const authAxios = axios.create({ baseURL: `${window.location.origin}` });

  authAxios.interceptors.request.use(
    config => {
      config.headers.Authorization = `Bearer ${accessToken}`;
      return config;
    },
    err => {
      return Promise.reject(err);
    }
  );

  authAxios.interceptors.response.use(
    response => {
      return response;
    },
    error => {
      const code = error && error.response ? error.response.status : 0;
      if (code === 401 || code === 500) {
        return getToken();
      } else {
        return Promise.reject(error);
      }
    }
  )

  return (
    <Provider value={{authAxios, isAdmin, canAdd, canManageAdasSubscriptions, canReadAll,
      canCreateSecurityCodes, canManageUsers, canManageAdasPackages, canManageAdasTargets,
      canManageIsoRigs}}>
      {children}
    </Provider>
  )
};

export { FetchContext, FetchProvider };
